import { useEffect, useRef } from 'preact/hooks';
import { useUnifiedWidget } from '../providers/UnifiedWidgetProvider';
import MicIcon from '../icons/Voice';

const SIZE = 180;
const AVATAR_SIZE = 112;
const BARS = 72;
const GAP = 6;
const MIN_BAR = 2;
const MAX_BAR = 28;
// Speech energy sits in the lower bins; the upper ones stay near silent, so only this fraction is spread around the ring
const SPEECH_BIN_FRACTION = 0.4;

type AgentAvatarWavesProps = {
  avatar?: string;
  colour: string;
  iconColour: string;
  active: boolean;
  connecting: boolean;
  getFrequencyData: () => Uint8Array;
}

export const AgentAvatarWaves = ({
  avatar,
  colour,
  iconColour,
  active,
  connecting,
  getFrequencyData,
}: AgentAvatarWavesProps) => {
  const { gooberCss } = useUnifiedWidget();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const getFrequencyDataRef = useRef(getFrequencyData);
  getFrequencyDataRef.current = getFrequencyData;
  const levelsRef = useRef<number[]>(new Array<number>(BARS).fill(0));

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const ratio = window.devicePixelRatio || 1;
    canvas.width = SIZE * ratio;
    canvas.height = SIZE * ratio;
    context.scale(ratio, ratio);

    const levels = levelsRef.current;
    const innerRadius = AVATAR_SIZE / 2 + GAP;
    let frame = 0;

    const draw = () => {
      let data: Uint8Array | null = null;
      if (active) {
        try {
          data = getFrequencyDataRef.current();
        } catch {}
      }
      const usableBins = data ? Math.floor(data.length * SPEECH_BIN_FRACTION) : 0;

      context.clearRect(0, 0, SIZE, SIZE);
      context.strokeStyle = colour;
      context.lineCap = 'round';
      context.lineWidth = 3;

      let moving = false;
      for (let i = 0; i < BARS; i++) {
        const mirrored = i < BARS / 2 ? i : BARS - 1 - i;
        const bin = Math.floor((mirrored / (BARS / 2)) * usableBins);
        const target = data && usableBins ? (data[bin] ?? 0) / 255 : 0;
        const level = levels[i]! + (target - levels[i]!) * 0.3;
        levels[i] = level;
        if (level > 0.01) moving = true;

        const length = MIN_BAR + level * (MAX_BAR - MIN_BAR);
        const angle = (i / BARS) * Math.PI * 2 - Math.PI / 2;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        context.globalAlpha = 0.35 + level * 0.65;
        context.beginPath();
        context.moveTo(SIZE / 2 + cos * innerRadius, SIZE / 2 + sin * innerRadius);
        context.lineTo(SIZE / 2 + cos * (innerRadius + length), SIZE / 2 + sin * (innerRadius + length));
        context.stroke();
      }

      if (active || moving) {
        frame = requestAnimationFrame(draw);
      }
    };

    draw();
    return () => cancelAnimationFrame(frame);
  }, [active, colour]);

  return (
    <div className={gooberCss({
      position: "relative",
      width: `${SIZE}px`,
      height: `${SIZE}px`,
      margin: "0 auto",
    })}>
      <canvas
        ref={canvasRef}
        className={gooberCss({
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: active || connecting ? 1 : 0.4,
          transition: "opacity 0.3s ease",
        })}
      />
      <div className={gooberCss({
        position: "absolute",
        top: "50%",
        left: "50%",
        width: `${AVATAR_SIZE}px`,
        height: `${AVATAR_SIZE}px`,
        marginTop: `-${AVATAR_SIZE / 2}px`,
        marginLeft: `-${AVATAR_SIZE / 2}px`,
        borderRadius: "50%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: colour,
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
        animation: connecting ? "avatarPulse 1.4s ease-in-out infinite" : "none",
        "@keyframes avatarPulse": {
          "0%, 100%": { transform: "scale(1)" },
          "50%": { transform: "scale(0.94)" },
        },
      })}>
        {avatar ? (
          <img
            src={avatar}
            alt=""
            className={gooberCss({
              width: "100%",
              height: "100%",
              objectFit: "cover",
            })}
          />
        ) : (
          <MicIcon className={gooberCss({ color: iconColour })} width={48} height={48} />
        )}
      </div>
    </div>
  );
};
